import { ModaSofa } from '@dora/backpack';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import * as FurnitureList from '../furnitureList';
import * as ProductData from './productData';

import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { FurnitureItem } from '../../data/furniture';

/** The raw data from the FurnitureList store */
export const furnitureList = (state: RootState) => state.furnitureList;

/**
 *
 * The furniture list hydrated with any product data
 *
 * TODO: rename this selector to withProductData
 */
export const furnitureListWithProducts = createSelector(
    furnitureList,
    ProductData.productBySkuFn,
    (furnitureList, productBySku) =>
        pipe(
            furnitureList,
            A.map<FurnitureList.Item, FurnitureItem>(
                ({ aryaId, productIds, configuration, summary }) => ({
                    aryaId,
                    products: productIds.map(
                        productId =>
                            productBySku?.(productId) ??
                            (() => {
                                throw new Error('existing furniture used nonexistent product');
                            })(),
                    ),
                    summary,
                    configuration,
                }),
            ),
        ),
);

/**
 * The furniture list containing only entries which have a modular sofa configuration
 */
export const withConfiguration = createSelector(furnitureList, furnitureList =>
    furnitureList.filter(item => O.isSome(item.configuration)),
);

/**
 * Select a function which checks to see if a furnitureId is associated with some configuration
 */
export const checkHasConfiguration = createSelector(
    withConfiguration,
    list => (furnitureId: string) => list.find(p => p.aryaId === furnitureId) !== undefined,
);

/**
 * Select a function which finds Configuration for a specific furniture ID
 */
export const findConfiguration = createSelector(
    withConfiguration,
    list =>
        (furnitureId: string): O.Option<ModaSofa.Configuration> =>
            pipe(
                list.find(p => p.aryaId === furnitureId),
                O.fromNullable,
                O.chain(x => x.configuration),
            ),
);

/**
 * The total number of fully formed furniture objects in the scene
 */
export const furnitureListLength = createSelector(furnitureList, list => list.length);
